import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Nav from './Nav';
import SearchButton from '../ui/SearchButton';
import MenuButton from '../ui/MenuButton';

const Header = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Calculate scroll progress for progress bar
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled_percentage = (window.scrollY / windowHeight) * 100;
      setScrollProgress(scrolled_percentage);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check initial scroll position and screen size
    handleScroll();
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine header style based on page and scroll
  const getHeaderStyle = () => {
    const baseStyle = {
      position: 'fixed' as const,
      top: '0',
      width: '100%',
      left: '0',
      right: '0',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    if (isMobile && isScrolled) {
      return {
        ...baseStyle,
        backgroundColor: '#0f1b3c',
        backdropFilter: 'blur(12px)',
      };
    }
    
    if (isHome && !isScrolled) {
      return {
        ...baseStyle,
        backgroundColor: '#0f1b3c',
        backdropFilter: 'blur(8px)',
      };
    } else if (isHome && isScrolled) {
      return {
        ...baseStyle,
        backgroundColor: 'rgba(15, 27, 60, 0.95)',
        backdropFilter: 'blur(16px) saturate(180%)',
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'white',
        backdropFilter: isScrolled ? 'blur(16px) saturate(180%)' : 'none',
      };
    }
  };

  const headerStyle = getHeaderStyle();
  const isDark = isHome || (isMobile && isScrolled);

  return (
    <>
      <header 
        className={`fixed left-0 right-0 z-50 w-full ${isScrolled ? 'shadow-xl' : ''}`}
        style={{ 
          ...headerStyle,
          minHeight: isScrolled ? '70px' : '80px',
          boxShadow: isScrolled 
            ? isDark 
              ? '0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 186, 23, 0.1)' 
              : '0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(15, 27, 60, 0.05)'
            : 'none',
          zIndex: 1000,
        }}
      >
        {/* Gold accent line at top */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, transparent, #ffba17, transparent)',
            opacity: isScrolled ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center justify-between" style={{ padding: isScrolled ? '0.75rem 0' : '1rem 0' }}>
            {/* Logo with animation */}
            <Link 
              to="/" 
              className="logo flex-shrink-0 relative group"
              style={{
                transition: 'transform 0.3s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <img 
                src={
                  !isMobile && isHome
                    ? "/assets/logo/logo2.png"
                    : isMobile && isScrolled
                    ? "/assets/logo/logo2.png"
                    : (isHome && !isScrolled ? "/assets/logo/logo2.png" : "/assets/logo/logo.png")
                } 
                alt="Tanzania Digital Awards"
                style={{ 
                  height: 'auto',
                  width: isScrolled ? '90px' : '110px',
                  maxWidth: '100%',
                  transition: 'all 0.4s ease',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))',
                }}
              />
              {/* Logo glow effect on hover */}
              <div 
                style={{
                  position: 'absolute',
                  inset: '-10px',
                  background: 'radial-gradient(circle, rgba(255, 186, 23, 0.15) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  zIndex: -1,
                }}
                className="group-hover:opacity-100"
              />
            </Link>

            {/* Navigation and Actions */}
            <div className="flex items-center gap-6">
              <Nav />
              
              {/* CTA Button - Desktop */}
              <div className="hidden lg:block">
                <Link
                  to="/nominate"
                  className="relative overflow-hidden group"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 1.5rem',
                    backgroundColor: '#ffba17',
                    color: 'white',
                    borderRadius: '9999px',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(255, 186, 23, 0.3)',
                    border: '2px solid transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 186, 23, 0.4)';
                    e.currentTarget.style.borderColor = '#ffba17';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = isDark ? '#ffba17' : '#0f1b3c';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 186, 23, 0.3)';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.backgroundColor = '#ffba17';
                    e.currentTarget.style.color = 'white';
                  }}
                >
                  <i className="fa-solid fa-trophy" style={{ fontSize: '0.875rem' }}></i>
                  <span className="font-semibold">NOMINATE NOW</span>
                  <i className="fa-solid fa-arrow-right" style={{ fontSize: '0.75rem', transition: 'transform 0.3s ease' }}></i>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <MenuButton 
                  isHome={isHome} 
                  isScrolled={isScrolled} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Progress Bar */}
        <div 
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '2px',
            width: `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #ffba17, #ffa500)',
            transition: 'width 0.1s ease-out',
            boxShadow: '0 0 10px rgba(255, 186, 23, 0.5)',
          }}
        />
      </header>

      {/* Spacer to prevent content jump */}
      <div style={{ height: isScrolled ? '70px' : '80px' }} />
    </>
  );
};

export default Header;
